import React, { useState, useEffect, useCallback } from "react";
import DashboardNav from "../DashboardNavbar/DashboardNav";
import BulkorderDetails from "../../components/modal/BulkorderDetails";
import { getAllBulkOrders } from "../../api/Api";
import {
  Box,
  Typography,
  Paper,
  Skeleton,
  Button,
  TextField,
  Chip,
  TablePagination,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import DataTable from "react-data-table-component"; // Assuming usage of react-data-table-component

const BulkOrders = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  // Fetch all bulk orders data
  const fetchBulkOrders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllBulkOrders();
      const fetchedOrders = response.data.result || [];
      setOrders(fetchedOrders);
      setFilteredOrders(fetchedOrders);
    } catch (error) {
      console.error("Error fetching bulk orders:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBulkOrders();
  }, [fetchBulkOrders]);

  // Filter orders based on date range
  useEffect(() => {
    if (!fromDate && !toDate) {
      setFilteredOrders(orders);
    } else {
      const filtered = orders.filter((order) => {
        const orderDate = new Date(order.createdAt);
        const from = fromDate ? new Date(fromDate) : null;
        const to = toDate ? new Date(toDate) : null;
        return (!from || orderDate >= from) && (!to || orderDate <= to);
      });
      setFilteredOrders(filtered);
    }
  }, [fromDate, toDate, orders]);

  // Column structure
  const columns = [
    {
      name: "Sl No",
      selector: (_, index) => index + 1,
      sortable: true,
      width: "5%",
    },
    {
      name: "Customer Name",
      selector: (row) => row.name,
      sortable: true,
      width: "10%",
    },
    {
      name: "Item Name",
      selector: (row) => row.itemName,
      sortable: true,
      width: "10%",
    },
    {
      name: "Quantity",
      selector: (row) => row.quantity,
      sortable: true,
      width: "10%",
    },
    {
      name: "Area",
      selector: (row) => row.area,
      sortable: true,
      width: "10%",
    },
    {
      name: "Order Placed",
      selector: (row) => new Date(row.createdAt).toLocaleDateString(),
      sortable: true,
      width: "10%",
    },
    {
      name: "Delivery Date",
      selector: (row) =>
        new Date(row.expectedDeliveryDate).toLocaleDateString(),
      sortable: true,
      width: "10%",
    },
    {
      name: "Payment Mode",
      selector: (row) => row.paymentMethod,
      sortable: true,
      width: "10%",
    },
    {
      name: "Payment Status",
      selector: (row) => (
        <Chip
          label={row.paymentStatus}
          color={row.paymentStatus === "Pending" ? "warning" : "success"}
        />
      ),
      sortable: true,
      width: "10%",
    },
    {
      name: "Actions",
      cell: (row) => (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="contained"
            color="info"
            onClick={() => handleMoreDetails(row)}
          >
            More Details
          </Button>
        </Stack>
      ),
      width: "15%",
    },
  ];

  // Handle modal actions
  const handleMoreDetails = (order) => {
    setSelectedOrder(order);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedOrder(null);
  };

  return (
    <Box sx={{ height: "100vh" }}>
      <Box sx={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000 }}>
        <DashboardNav />
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: "180px",
          left: 0,
          right: 0,
          px: 1,
          width: "90vw",
          margin: "auto",
          color: "#888585",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: "500",
            fontSize: "32px",
            fontFamily: theme.typography.fontFamily,
          }}
          gutterBottom
        >
          Bulk Order Dashboard
        </Typography>

        {/* Filter Row */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            gap: "5vw",
            mt: 6,
            mb: 4,
          }}
        >
          <TextField
            label="From Date"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ width: "25%" }}
          />
          <TextField
            label="To Date"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ width: "25%" }}
          />
        </Box>

        {/* Data Table */}
        <Paper>
          {loading ? (
            Array.from(new Array(5)).map((_, index) => (
              <Skeleton
                key={index}
                variant="text"
                width="100%"
                height={40}
                sx={{ mb: 1 }}
              />
            ))
          ) : (
            <DataTable
              columns={columns}
              data={filteredOrders}
              pagination
              paginationRowsPerPageOptions={[5, 10, 15]}
              customStyles={{
                header: {
                  style: {
                    backgroundColor: theme.palette.primary.light,
                    color: "#fff",
                    textAlign: "center",
                  },
                },
                rows: {
                  style: {
                    minHeight: "50px", // Adjust row height
                    textAlign: "center",
                  },
                },
                headCells: {
                  style: {
                    backgroundColor: theme.palette.primary.light,
                    color: "#fff",
                    textAlign: "center",
                  },
                },
                cells: {
                  style: {
                    paddingLeft: "8px", // Cell padding for data
                    paddingRight: "8px",
                    textAlign: "center",
                  },
                },
                pagination: {
                  style: {
                    borderTop: `1px solid ${theme.palette.divider}`,
                    padding: "8px",
                  },
                },
              }}
              fixedHeader
              fixedHeaderScrollHeight="400px" // Set table height
              noDataComponent={
                <Typography variant="body2" sx={{ textAlign: "center", py: 2 }}>
                  No Data Available
                </Typography>
              }
            />
          )}
        </Paper>

        {selectedOrder && (
          <BulkorderDetails
            open={modalOpen}
            onClose={handleCloseModal}
            orderData={selectedOrder}
          />
        )}
      </Box>
    </Box>
  );
};

export default BulkOrders;
